import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatBuyType, formatMapName } from '../../liveSession/lib/formatters';
import { formatStrategyType } from '../lib/formatPlaybook';
import { createStrategy, duplicateStrategy, listStrategies } from '../strategyApi';
import type { PlaybookSummary } from '../types';
import AuthorGate from './AuthorGate';
import styles from './author.module.css';

type Filters = {
  q: string;
  map: string;
  type: string;
  side: string;
  status: string;
};

const EMPTY: Filters = { q: '', map: 'all', type: 'all', side: 'all', status: 'all' };

function mapKey(map: string) {
  return map.trim().toLowerCase().replace(/^de_/, '');
}

export default function StrategyStudioPage() {
  return (
    <AuthorGate crumb="Studio">
      {(team) => <Studio teamId={team.id} />}
    </AuthorGate>
  );
}

function Studio({ teamId }: { teamId: string }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PlaybookSummary[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listStrategies(teamId)
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load strategies');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const maps = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of rows) {
      const key = mapKey(row.map);
      if (key && !seen.has(key)) seen.set(key, formatMapName(row.map) || row.map);
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const visible = useMemo(() => {
    const needle = filters.q.trim().toLowerCase();
    return rows.filter((row) => {
      const blob = `${row.name} ${row.id} ${row.description ?? ''} ${row.route ?? ''} ${row.site ?? ''} ${formatMapName(row.map)}`.toLowerCase();
      if (needle && !blob.includes(needle)) return false;
      if (filters.map !== 'all' && mapKey(row.map) !== filters.map) return false;
      if (filters.type !== 'all' && (row.strategyType ?? '') !== filters.type) return false;
      if (filters.side !== 'all' && row.side.trim().toUpperCase() !== filters.side) return false;
      if (filters.status !== 'all' && row.status.trim().toLowerCase() !== filters.status) return false;
      return true;
    });
  }, [rows, filters]);

  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((row) => row.status === 'active').length,
    draft: rows.filter((row) => row.status === 'draft').length,
  }), [rows]);

  async function createNew() {
    setBusy(true);
    setError(null);
    try {
      const created = await createStrategy(teamId);
      navigate(`/studio/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create a strategy');
      setBusy(false);
    }
  }

  async function copy(id: string) {
    setBusy(true);
    setError(null);
    try {
      const created = await duplicateStrategy(teamId, id);
      navigate(`/studio/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not duplicate the strategy');
      setBusy(false);
    }
  }

  const filtersOn = filters.q.trim() !== '' || filters.map !== 'all' || filters.type !== 'all' || filters.side !== 'all' || filters.status !== 'all';

  return (
    <div className={styles.studio}>
      <div className={styles.head}>
        <div>
          <div className={styles.eyebrow}>Write the playbook</div>
          <h1>Strategy studio</h1>
          <p>Write the calls the team will learn and run. Drafts stay here until you set them active.</p>
        </div>
        <button type="button" className={styles.primary} onClick={() => void createNew()} disabled={busy}>
          New strategy
        </button>
      </div>

      <section className={styles.stats} aria-label="Playbook counts">
        <div className={styles.stat}>
          <div>
            <div className={styles.micro}>In the book</div>
            <p>Drafts, active and inactive</p>
          </div>
          <strong>{counts.all}</strong>
        </div>
        <div className={`${styles.stat} ${styles.known}`}>
          <div>
            <div className={styles.micro}>Active</div>
            <p>Players can learn these</p>
          </div>
          <strong>{counts.active}</strong>
        </div>
        <div className={`${styles.stat} ${styles.pending}`}>
          <div>
            <div className={styles.micro}>Drafts</div>
            <p>Still being written</p>
          </div>
          <strong>{counts.draft}</strong>
        </div>
      </section>

      <section className={styles.studioPanel} aria-label="Strategy studio">
        <div className={styles.filterArea}>
          <input
            className={styles.search}
            type="search"
            aria-label="Search strategies"
            value={filters.q}
            onChange={(event) => setFilters({ ...filters, q: event.target.value })}
            placeholder="Search by call, route or ID…"
            autoComplete="off"
          />
          <div className={styles.filterRow}>
            <label>
              Map
              <select value={filters.map} aria-label="Map" onChange={(event) => setFilters({ ...filters, map: event.target.value })}>
                <option value="all">All maps</option>
                {maps.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>
              Type
              <select value={filters.type} aria-label="Type" onChange={(event) => setFilters({ ...filters, type: event.target.value })}>
                <option value="all">All types</option>
                {['execute', 'default', 'rush', 'retake', 'hold'].map((type) => (
                  <option key={type} value={type}>{formatStrategyType(type)}</option>
                ))}
              </select>
            </label>
            <label>
              Side
              <select value={filters.side} aria-label="Side" onChange={(event) => setFilters({ ...filters, side: event.target.value })}>
                <option value="all">Both sides</option>
                <option value="T">T</option>
                <option value="CT">CT</option>
              </select>
            </label>
            <label>
              Status
              <select value={filters.status} aria-label="Status" onChange={(event) => setFilters({ ...filters, status: event.target.value })}>
                <option value="all">Any status</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <button type="button" className={styles.reset} onClick={() => setFilters(EMPTY)} disabled={!filtersOn}>
              Reset filters
            </button>
          </div>
        </div>

        <div className={styles.resultsBar}>
          <span role="status">{visible.length} of {rows.length} strategies</span>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {loading && <p className={styles.studioMessage}>Loading strategies…</p>}
        {!loading && visible.length === 0 && (
          <div className={styles.studioEmpty}>
            <div className={styles.eyebrow}>No matching calls</div>
            <h2>No strategies found</h2>
            <p>Try a different search or broaden your filters.</p>
          </div>
        )}

        {!loading && visible.length > 0 && (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th scope="col">Strategy</th>
                  <th scope="col">Map / side</th>
                  <th scope="col">Type</th>
                  <th scope="col">Buy</th>
                  <th scope="col">Status</th>
                  <th scope="col">Makeup</th>
                  <th scope="col"><span className={styles.micro}>Edit</span></th>
                  <th scope="col"><span className={styles.micro}>Copy</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const status = row.status.trim().toLowerCase();
                  const side = row.side.trim().toUpperCase();
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className={styles.strategyCell}>
                          <Link to={`/studio/${row.id}`}>{row.name}</Link>
                        </div>
                        <p className={styles.rowDescription}>{row.description?.trim() || 'No description yet.'}</p>
                      </td>
                      <td>
                        <div className={styles.mapName}>{formatMapName(row.map) || row.map}</div>
                        <span className={sideBadge(side)}>{side || '—'} side</span>
                      </td>
                      <td><span className={styles.chip}>{formatStrategyType(row.strategyType) || 'Not set'}</span></td>
                      <td>{formatBuyType(row.buyType) || '—'}</td>
                      <td><span className={statusBadge(status)}>{statusLabel(status)}</span></td>
                      <td>{row.roleCount} {row.roleCount === 1 ? 'role' : 'roles'}</td>
                      <td>
                        <Link className={styles.edit} to={`/studio/${row.id}`} aria-label={`Edit ${row.name}`}>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4z" />
                          </svg>
                        </Link>
                      </td>
                      <td>
                        <button type="button" className={styles.duplicate} onClick={() => void copy(row.id)} disabled={busy}>
                          Duplicate
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.listFooter}>
          <span>Players learn active strategies. Drafts and inactive calls stay in the studio.</span>
          <span>Duplicate keeps the same lineups</span>
        </div>
      </section>
      <p className={styles.footerNote}>A lineup is written once. Strategies point at it.</p>
    </div>
  );
}

function statusLabel(status: string) {
  if (status === 'active') return 'Active';
  if (status === 'inactive') return 'Inactive';
  if (status === 'draft') return 'Draft';
  return status || 'Draft';
}

function statusBadge(status: string) {
  const tone = status === 'active' ? styles.active
    : status === 'inactive' ? styles.inactive
      : status === 'draft' ? styles.draft
        : undefined;
  return tone ? `${styles.chip} ${tone}` : styles.chip;
}

function sideBadge(side: string) {
  const tone = side === 'T' ? styles.t : side === 'CT' ? styles.ct : undefined;
  return tone ? `${styles.chip} ${tone}` : styles.chip;
}
